<script lang="ts">
    import { getContext, onMount } from "svelte";
    import type { Writable } from "svelte/store";
    import type { SkinState, State } from "../../../types/state";
    import {
        formatAudioTrack,
        formatTextTrack,
        formatVideoTrack,
    } from "../../../utils/tracks";
    let state: Writable<State> = getContext("playerState");
    let skinState: Writable<SkinState> = getContext("skinState");

    let stateShowInfoBox = $skinState.showInfoBox;

    import Button from "../../shared/Button.svelte";
    import Icon from "../../shared/Icon.svelte";
    import VideoTracksMenu from "./subMenus/VideoTracksMenu.svelte";
    import AudioTracksMenu from "./subMenus/AudioTracksMenu.svelte";
    import TextTracksMenu from "./subMenus/TextTracksMenu.svelte";
    import MiscMenu from "./subMenus/MiscMenu.svelte";
    import ErrorsMenu from "./subMenus/ErrorsMenu.svelte";

    let selectedTab;
    const titleMappings = {
        audio: "Audio track",
        video: "Video quality",
        text: "Subtitles",
        misc: "Misc",
        errors: "Errors",
    };

    $: overlayTitle = titleMappings[selectedTab];

    function showSubOverlay(subMenuType: string) {
        $skinState.isSubMenuOpen = true;
        selectedTab = subMenuType;
    }

    function showRootMenu() {
        $skinState.isSubMenuOpen = false;
    }

    function toggleShowInfoBox() {
        $stateShowInfoBox = !$stateShowInfoBox;
    }
</script>

{#if $skinState.isSettingsOpen}
    <div class="scotparl-settings-overlay">
        {#if !$skinState.isSubMenuOpen}
            <div class="overlay rootMenu">
                <div
                    class="rootMenu--category clickable"
                    on:click={() => showSubOverlay("video")}
                    aria-label="open video track settings"
                >
                    <Button
                        label="open video track settings"
                        on:click={() => showSubOverlay("video")}
                    >
                        <Icon type="videoQuality" />
                    </Button>
                    <span>
                        {formatVideoTrack(
                            $state?.activeVideoTrack,
                            $state.adapter?.abr ?? true
                        )}
                    </span>
                </div>
                <div
                    class="rootMenu--category clickable"
                    on:click={() => showSubOverlay("audio")}
                    aria-label="open audio track settings"
                >
                    <Button
                        label="open audio track settings"
                        on:click={() => showSubOverlay("audio")}
                    >
                        <Icon type="speaker" />
                    </Button>
                    <span>
                        {formatAudioTrack($state?.activeAudioTrack)}
                    </span>
                </div>
                <div
                    class="rootMenu--category clickable"
                    on:click={() => showSubOverlay("text")}
                    aria-label="open text track settings"
                >
                    <Button
                        label="open text track settings"
                        on:click={() => showSubOverlay("text")}
                    >
                        <Icon type="captions" />
                    </Button>
                    <span>
                        {formatTextTrack($state?.activeTextTrack)}
                    </span>
                </div>

                {#if $skinState.showHiddenMenu}
                    <div
                        class="rootMenu--category clickable"
                        on:click={toggleShowInfoBox}
                        aria-label="Toggle info box"
                    >
                        <input
                            type="checkbox"
                            bind:checked={$stateShowInfoBox}
                            class="rootMenu--showInfo"
                            aria-label="Toggle info box"
                        />
                        <span> Info box</span>
                    </div>
                    <div
                        class="rootMenu--category clickable"
                        on:click={() => showSubOverlay("misc")}
                        aria-label="open misc menu"
                    >
                        <Button
                            label="open misc menu"
                            on:click={() => showSubOverlay("misc")}
                        >
                            <Icon type="miscellaneous" />
                        </Button>
                        <span>Miscellaneous</span>
                    </div>

                    <div
                        class="rootMenu--category clickable"
                        on:click={() => showSubOverlay("errors")}
                        aria-label="open errors menu"
                    >
                        <Button
                            label="open errors menu"
                            on:click={() => showSubOverlay("errors")}
                        >
                            <Icon type="errorOutline" />
                        </Button>
                        <span>Errors ({$state.errors.length})</span>
                    </div>
                {/if}
            </div>
        {:else}
            <div class="overlay subMenu">
                <div
                    class="backMenu clickable"
                    on:click={showRootMenu}
                    aria-label="Back to settings menu"
                >
                    <Button
                        label="Back to settings menu"
                        on:click={showRootMenu}
                    >
                        <Icon type="caretLeft" />
                    </Button>
                    <span>{overlayTitle}</span>
                </div>
                <div class="tabContent" class:visible={selectedTab === "video"}>
                    <VideoTracksMenu />
                </div>
                <div class="tabContent" class:visible={selectedTab === "audio"}>
                    <AudioTracksMenu />
                </div>
                <div class="tabContent" class:visible={selectedTab === "text"}>
                    <TextTracksMenu />
                </div>
                <div class="tabContent" class:visible={selectedTab === "misc"}>
                    <MiscMenu />
                </div>
                <div
                    class="tabContent"
                    class:visible={selectedTab === "errors"}
                >
                    <ErrorsMenu />
                </div>
            </div>
        {/if}
    </div>
{/if}

<style lang="scss">
    @import "./../../../globals/_global-variables.scss";

    .scotparl-settings-overlay {
        position: absolute;
        width: 100%;
        height: calc(100% - 4em);
        color: #fff;
        box-sizing: border-box;
        top: 0;
        display: flex;
        padding: 1em;
        box-sizing: border-box;
        background-color: rgba(0, 0, 0, 0.5);

        .overlay {
            width: 100%;
            overflow-y: auto;
            overflow-x: visible;

            padding: 5px; // TODO: Review if really needed

            display: block;
            position: relative;
        }

        .rootMenu--showInfo {
            height: 1em;
            width: 3em;
            margin: 0;
            padding: 0.3em;
        }

        .rootMenu--category {
            display: flex;
            align-items: center;
            min-height: 3em;
        }

        .subMenu {
            .backMenu {
                display: flex;
                align-items: center;
                border-bottom: 1px solid #bebebe;
                min-height: 3em;
            }
        }
    }
    /* Style the tab content */
    .tabContent {
        display: none;
        width: 100%;
        border-top: none;
        height: calc(100% - 4em - 1px);
        padding-top: 1em;

        &.visible {
            display: block;
        }
    }

    .clickable {
        cursor: pointer;
        border-radius: 0.1em;
        &:hover {
            background-color: var(
                --v-button-focus-background-color,
                $button-focus-background-color
            );
        }
    }
</style>
